import { Center, Spinner } from '@chakra-ui/react'
import { useActiveContentContext } from '@src/contexts/ActiveContentContext'
import { useCurrentUser } from '@src/hooks/useCurrentUser'
import { QueryStateSet, useQueryStateSet } from '@src/hooks/useQueryStateSet'
import { parseAsArrayOf, parseAsBoolean, parseAsFloat, parseAsInteger, parseAsString } from 'nuqs'
import React, { createContext, FC, ReactNode, useContext } from 'react'

export type InvestorSearchFilters = {
  investmentStages?: string[]
  hqDistanceInMiles?: number
  hqLocation?: any
  hqLongitude?: any
  hqLatitude?: any
  hqLocationRegion?: string
  hqLocationCountry?: string
  investorTypes?: string[]
  isSavedByUser?: boolean
  isVerified?: boolean
  keywords?: string
  matchAll?: boolean
  maximumInvestmentInDollars?: number
  minimumInvestmentInDollars?: number
  preferredIndustries?: string[]
  preferredIndustriesAndVerticals?: string[]
  preferredVerticals?: string[]
}

export type InvestorSearchPagination = {
  order?: string
  page?: string
  pageSize?: string
  sortDirection?: string
  sortField?: string
}

type InvestorSearchContextValue = {
  filterValues?: InvestorSearchFilters
  filters?: QueryStateSet
  pagination?: QueryStateSet
  paginationValues?: InvestorSearchPagination
  breadcrumbs?: {
    title: string
    href: string
  }[]
  setFilter?: (key: string, value: any) => any
  setBreadcrumbs?: (breadcrumbs: InvestorSearchContextValue['breadcrumbs']) => void
}

export const InvestorSearchContext = createContext<InvestorSearchContextValue>({})

export const useInvestorSearchContext = () => useContext(InvestorSearchContext)

export const InvestorSearchContextProvider: FC<
  InvestorSearchContextValue & { children?: ReactNode }
> = props => {
  const activeContent = useActiveContentContext()
  const { currentUser } = useCurrentUser()

  const [pagination, paginationValues] = useQueryStateSet<InvestorSearchPagination>({
    page: parseAsInteger.withDefault(1),
    pageSize: parseAsInteger.withDefault(50),
    sortField: parseAsString.withDefault('isVerified'),
    sortDirection: parseAsString.withDefault('desc'),
  })

  const [filters, filterValues] = useQueryStateSet<InvestorSearchFilters>({
    investmentStages: parseAsArrayOf(parseAsString),
    hqDistanceInMiles: parseAsInteger,
    hqLatitude: parseAsFloat,
    hqLocation: parseAsString,
    hqLongitude: parseAsFloat,
    hqLocationCountry: parseAsString,
    hqLocationRegion: parseAsString,
    investorTypes: parseAsArrayOf(parseAsString),
    isVerified: parseAsBoolean,
    keywords: parseAsString,
    matchAll: parseAsBoolean.withDefault(true),
    maximumInvestmentInDollars: parseAsString,
    minimumInvestmentInDollars: parseAsString,
    preferredIndustries: parseAsArrayOf(parseAsString),
    preferredVerticals: parseAsArrayOf(parseAsString),
    preferredIndustriesAndVerticals: parseAsArrayOf(parseAsString),
  })

  const contextValue = {
    filters,
    setFilter: (key, value) => {
      if (filters[key]) {
        filters[key].setValue(value)
      } else {
        alert(`InvestorSearchContext.filters.${key} is not defined!`)
      }
    },
    filterValues,
    pagination,
    paginationValues,
    activeContent,
  }

  if (!currentUser) {
    return (
      <Center padding={'1em'}>
        <Spinner />
      </Center>
    )
  }

  return (
    <InvestorSearchContext.Provider value={contextValue}>{props.children}</InvestorSearchContext.Provider>
  )
}
